import { router } from '@inertiajs/react'
import { useState } from 'react'
import { Copy as CopyIcon, ImageIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface Props {
  worker: { id: string; instance_name: string; organization_slug: string }
  fleet_machines: { slug: string; name: string }[]
  currentMachineSlug: string | null
  sourceHasAvatar: boolean
}

type Tone = 'success' | 'neutral'

const PILL_CLASSES: Record<Tone, string> = {
  success: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  neutral: 'bg-accent text-muted-foreground',
}

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${PILL_CLASSES[tone]}`}>
      {children}
    </span>
  )
}

export default function CloneTab({
  worker,
  fleet_machines,
  currentMachineSlug,
  sourceHasAvatar,
}: Props) {
  const targets = fleet_machines.filter((m) => m.slug !== currentMachineSlug)
  const [targetSlug, setTargetSlug] = useState(targets[0]?.slug || '')
  const [targetOrgSlug, setTargetOrgSlug] = useState(worker.organization_slug)
  const [targetInstanceName, setTargetInstanceName] = useState(`${worker.instance_name}-copy`)
  const [submitting, setSubmitting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const sameAsSource = targetInstanceName === worker.instance_name
  const canSubmit = !!targetSlug && !!targetOrgSlug && !!targetInstanceName && !sameAsSource

  const submitClone = () => {
    if (!canSubmit) return
    setConfirmOpen(true)
  }

  const executeClone = () => {
    setSubmitting(true)
    router.post(
      `/cc/instances/${worker.id}/clone`,
      {
        target_machine_slug: targetSlug,
        target_org_slug: targetOrgSlug,
        target_instance_name: targetInstanceName,
      },
      { preserveScroll: true, onFinish: () => setSubmitting(false) },
    )
  }

  if (targets.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <CopyIcon className="h-8 w-8 text-muted-foreground/60 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          Nenhuma máquina destino disponível. Cadastre outra FleetMachine em <code className="font-mono">/cc/fleet_machines</code> para habilitar a cópia.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <section className="bg-card border border-border rounded-lg overflow-hidden">
        <header className="flex items-center justify-between gap-3 px-4 py-3 bg-muted border-b border-border">
          <h3 className="text-sm font-semibold text-foreground inline-flex items-center gap-2">
            <CopyIcon className="h-4 w-4" /> Cópia para outra máquina
          </h3>
          {sourceHasAvatar
            ? <Pill tone="success"><ImageIcon className="h-3 w-3" />avatar será copiado</Pill>
            : <Pill tone="neutral">source sem avatar</Pill>}
        </header>
        <div className="p-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            Cria uma instância nova no destino e copia <code className="font-mono">config/</code> + <code className="font-mono">workspace/</code> via rsync. Nenhuma migração apply é executada.
          </p>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="block">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">FleetMachine destino</div>
              <select
                value={targetSlug}
                onChange={(e) => setTargetSlug(e.target.value)}
                className="w-full py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {targets.map((m) => (
                  <option key={m.slug} value={m.slug}>{m.name} ({m.slug})</option>
                ))}
              </select>
            </label>

            <label className="block">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Organização destino</div>
              <input
                value={targetOrgSlug}
                onChange={(e) => setTargetOrgSlug(e.target.value)}
                className="w-full py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                placeholder="org-slug"
              />
            </label>

            <label className="block md:col-span-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Novo instance_name</div>
              <input
                value={targetInstanceName}
                onChange={(e) => setTargetInstanceName(e.target.value)}
                className="w-full py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                placeholder="nome-da-copia"
              />
              {sameAsSource && (
                <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  Cópia precisa de um nome novo. Para manter o mesmo nome em outra máquina, use a aba Migração.
                </div>
              )}
            </label>
          </div>

          <div className="bg-muted/40 border border-border rounded-md px-3 py-2 text-xs">
            <span className="text-muted-foreground">Destino final: </span>
            <code className="font-mono text-foreground">{targetOrgSlug || 'org'}/{targetInstanceName || 'nome'}</code>
            <span className="text-muted-foreground"> em </span>
            <code className="font-mono text-foreground">{targetSlug || 'máquina'}</code>
          </div>

          <div className="flex justify-end">
            <Button onClick={submitClone} disabled={!canSubmit || submitting}>
              <CopyIcon className="h-4 w-4 mr-1" />
              {submitting ? 'Enfileirando…' : 'Criar cópia'}
            </Button>
          </div>
        </div>
      </section>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Criar cópia</AlertDialogTitle>
            <AlertDialogDescription>
              Criar cópia de "{worker.instance_name}" em {targetOrgSlug}/{targetInstanceName} na máquina "{targetSlug}"? Cria uma nova instância no destino, copia config/workspace para migration-sources, copia avatar se houver e não executa apply de migração.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false)
                executeClone()
              }}
            >
              Criar cópia
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
